import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FilterChips } from '~/components/FilterChips';
import { Pagination } from '~/components/Pagination';
import { Table } from '~/components/Table';
import { usePagination } from '~/hooks/usePagination';
import { useSort } from '~/hooks/useSort';
import { useURLFilters } from '~/modules/ddex-console/hooks/useURLFilters';
import { Breadcrumbs } from '~/modules/navigation/components/Breadcrumbs';
import { translateOptions } from '~settings/utils/helpers.ts';

import type { DdexImport } from '../api/lib/schemas';
import { Status } from '../api/lib/types';
import { useImportsHistory } from '../api/queries/useImportsHistory';
import { useImportStatusCounts } from '../api/queries/useImportStatusCounts';
import { DDEX_ROUTES } from '../config/routes';
import { DDEX_DEFAULT_PAGE_SIZE } from '../constants';
import { useImportsColumns } from './useImportsColumns';

const URL_FILTERS = ['statuses'];
const FILTERS = [
  {
    label: 'all',
    value: 'all',
  },
  {
    label: 'status.success',
    value: Status.success,
  },
  {
    label: 'status.needsAttention',
    value: Status.needs_attention,
  },
  {
    label: 'status.failed',
    value: Status.failed,
  },
  {
    label: 'status.processing',
    value: Status.processing,
  },
];

export function DdexConsole() {
  const { t } = useTranslation('ddex-console');
  const navigate = useNavigate();
  const filters = useURLFilters(URL_FILTERS);
  const columns = useImportsColumns();
  const { sorting, setSorting, sortParams } = useSort('order_by', 'order');

  const { page, setPageNumber, perPage, skip, take } = usePagination(
    DDEX_DEFAULT_PAGE_SIZE,
  );

  const { importsHistoryList, isLoading, isError } = useImportsHistory({
    skip,
    take,
    ...sortParams,
    ...filters,
  });

  const { importStatusCounts } = useImportStatusCounts();

  const translatedFilters = translateOptions(FILTERS, t);

  const filtersWithCounts = translatedFilters.map((filter) => {
    const count =
      filter.value === 'all'
        ? importStatusCounts?.all
        : importStatusCounts?.status_counts?.[filter.value as Status];

    return {
      ...filter,
      label: count === undefined ? filter.label : `${filter.label} (${count})`,
    };
  });

  const handleRowClick = (row: DdexImport) => {
    if (row.downstreamId) {
      navigate(DDEX_ROUTES.DDEX_RELEASE_DETAILS(row.downstreamId));
    }
  };

  if (isLoading) {
    return <section className="py-md">{t('loading')}</section>;
  }

  return (
    <>
      <Breadcrumbs.Root>
        <Breadcrumbs.Item to={DDEX_ROUTES.ROOT}>
          {t('release')}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item to={DDEX_ROUTES.DDEX_LIST}>
          {t('title')}
        </Breadcrumbs.Item>
      </Breadcrumbs.Root>
      <header className="flex items-center gap-md">
        <h1 className="py-md text-display-sm font-semibold">{t('title')}</h1>
      </header>
      <section className="flex justify-start pt-md">
        <FilterChips options={filtersWithCounts} path="statuses" />
        <Pagination
          page={page}
          total={importsHistoryList?.total ?? 0}
          perPage={perPage}
          setPageNumber={setPageNumber}
          entity="imports"
        />
      </section>
      <section className="py-sm">
        {isError ? (
          // eslint-disable-next-line i18next/no-literal-string
          <div role="alert">Something went wrong.</div>
        ) : (
          <Table
            data={importsHistoryList?.data ?? []}
            columns={columns}
            sort={{ sorting, onSortingChange: setSorting }}
            onRowClick={handleRowClick}
          />
        )}
      </section>
    </>
  );
}

DdexConsole.displayName = 'DdexConsole';
